import { useLDClient, withLDConsumer } from 'launchdarkly-react-client-sdk';
import React, { useEffect } from 'react';
import { Switch, Redirect, Route, Prompt } from 'react-router-dom';

import { useGetUserQuery } from 'types/dashboard-core-gqlTypes.generated.d';

import { AdvanceRateRouter } from './AdvanceRate';
import { ArrangmentGroupsRoutes } from './ArrangmentGroups';
import { AssignmentRouter } from './Assignment';
import { CustomersRouter } from './Customers';
import { LoanAccountsRouter } from './LoanAccounts';
import { SettingsRouter } from './settings/Router';
import { isQAUser } from 'packages/common-frontend/borrowerEnabledConfig';
import { heapIdentify } from 'packages/common-frontend/hooks/useHeap';
import DataMetadataProvider from 'valleyBank/domains/page/hooks/DataMetadataContext';
import UserRoleTypePremissionsProvider, {
  useUserRoleTypePermissionsContext,
} from 'valleyBank/domains/page/hooks/useUserRoleTypePermissions';

const ValleyBankInnerRouter = withLDConsumer()(() => {
  const { permissions } = useUserRoleTypePermissionsContext();
  const { data: userData } = useGetUserQuery();
  const ldClient = useLDClient();

  useEffect(() => {
    if (ldClient !== undefined && userData?.user !== undefined) {
      ldClient.identify({
        kind: 'user',
        key: userData.user!.id,
        orgSlug: userData.user!.organization.slug,
        roleType: userData.user!.roleType,
        isInternalFinleyUser: isQAUser(userData.user!.email),
      });
    }
  }, [ldClient, userData]);

  if (!userData || !userData.user) {
    return <></>;
  }

  const { user } = userData;

  // identifies heap analytics info to logged in customer's email
  if (user && window.heap?.identify) {
    heapIdentify({ dashboardType: 'securitiesProvider', user });
  }

  return (
    /**
     * Although the DataMetadataProvider is not used for the settings + advance-rate route, need to keep it at top level because the ValleyBankTemplate is shared across all routes
     */
    <DataMetadataProvider permissions={permissions}>
      <Switch>
        <Route exact path="/">
          <Redirect to="/loan-accounts" />
        </Route>

        <Route path="/loan-accounts" component={LoanAccountsRouter} />

        <Route path="/customers" component={CustomersRouter} />

        <Route path="/arrangement-groups" component={ArrangmentGroupsRoutes} />

        <Route path="/assignment">
          <Prompt message="Are you sure you want to leave this page? Any changes not submitted to the queue will be lost." />
          <AssignmentRouter />
        </Route>

        <Route path="/advance-rate" component={AdvanceRateRouter} />

        <Route path="/settings">
          <SettingsRouter />
        </Route>
      </Switch>
    </DataMetadataProvider>
  );
});

export const ValleyBankRouter = () => {
  return (
    <UserRoleTypePremissionsProvider>
      <ValleyBankInnerRouter />
    </UserRoleTypePremissionsProvider>
  );
};
